'use client';

import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import useQuiz from 'src/hooks/useQuiz';
import QuizQuestion from './QuizLayout/QuizQuestion';
import { Loading, useWindowDimensions } from '@get-mosh/msh-shared';
import { useParams, useSearchParams } from 'next/navigation';
import {
    PrefillById,
    QUIZ_HIDE_CONTINUE_QUESTION_TYPES,
    QUIZ_LONG_CONTENT_QUESTION_TYPES,
    QUIZ_QUESTION_TYPES,
    QUIZ_SKIP_NAVIGATION_QUESTION_TYPES,
    QuestionList,
    QuestionRecord,
    QuizFlow,
    QuizRecord,
} from 'src/types/Quiz';
import { QuizContext, QuizContextType } from './Quiz.context';
import {
    trackCompletedQuiz,
    trackGateTriggered,
    useTrackPage,
} from 'src/analytics/Events';
import { Vertical } from 'src/constants/Verticals';
import { QUIZ_SELECT_SMALL_ANSWER_LIMIT } from 'src/components/QuizQuestion/QuizSelectField/QuizSelectField';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PageType } from 'src/analytics/EventProps';
import { getQuizBaseUrl } from 'src/helpers/quiz';
import { useStartQuiz } from 'src/hooks/useStartQuiz';
import { SubmitAnswerParams, useSubmitAnswer } from 'src/hooks/useSubmitAnswer';
import { env } from 'src/env.mjs';

const queryClient = new QueryClient();
const LARGE_SCREEN_BREAKPOINT = 992;

export type QuizProps = {
    auspostPostcodeSearchEnabled: boolean;
    quizFlow: QuizFlow;
};

const Quiz = ({ auspostPostcodeSearchEnabled, quizFlow }: QuizProps) => {
    const [loading, setLoading] = useState(false);
    const params = useParams();
    const searchParams = useSearchParams();
    const quizType = params.type as Vertical;
    const initialQuizToken = searchParams?.get('quiz_token') || '';
    const initialQuizVersion = searchParams?.get('quiz_version') || '';
    const initialQuizSubVersion = searchParams?.get('quiz_sub_version') || '';
    const initialQuizResumeSource =
        searchParams?.get('quiz_resume_source') || '';
    const subscriptionId = searchParams?.get('subscription_id') || '';
    const referralCode = searchParams?.get('referral_code') || '';

    const { data: quizResponse, isLoading: startQuizLoading } = useStartQuiz({
        quizFlow,
        quizType,
        quizToken: initialQuizToken,
        quizVersion: initialQuizVersion,
        quizSubVersion: initialQuizSubVersion,
        quizResumeSource: initialQuizResumeSource,
        subscriptionId,
        referralCode,
    });

    const {
        quizToken,
        quizName,
        quizVersion,
        iterationOfProgressBar,
        currentQuestionId: quizCurrentQuestionId,
        questionById,
        questionList,
        submittedQuestionIds,
        setSubmittedQuestionIds,
        prefillById,
        prefillQuestionIds,
        generateQuestionList,
        generatePrefillData,
        medications,
        plans,
        sectionById,
        sectionList,
        quizSubVersion,
        quizResumeSource,
    } = useQuiz({
        quizFlow,
        quizResponse,
    });

    const { mutate: submitAnswer } = useSubmitAnswer({
        quizFlow,
        quizType,
        quizToken: initialQuizToken,
        quizVersion: initialQuizVersion,
        submittedQuestionIds,
        setSubmittedQuestionIds,
    });

    const vertical = Vertical[quizType.toUpperCase().replace(/-/g, '_')];

    const quizBaseUrl = getQuizBaseUrl({
        quizFlow,
        quizType,
        quizToken: quizToken ?? initialQuizToken ?? '',
        quizVersion: quizVersion ?? initialQuizVersion ?? '',
        subscriptionId,
        quizSubVersion,
        quizResumeSource,
    });

    useTrackPage(
        `${vertical} ${quizFlow} Quiz`,
        {
            vertical,
            page_type: PageType.QUIZ,
            version: quizVersion,
            quiz_token: quizToken,
            url:
                typeof window !== 'undefined'
                    ? window.location.origin + quizBaseUrl
                    : '',
            search: '?' + quizBaseUrl.split('?')[1],
            country_code: env.NEXT_PUBLIC_COUNTRY_CODE,
        },
        {
            disabled: !quizToken,
        },
    );

    // Determine if on mobile
    const [isMobile, setIsMobile] = useState(false);
    const { width } = useWindowDimensions();
    useEffect(() => {
        setIsMobile(width < LARGE_SCREEN_BREAKPOINT);
    }, [width]);

    // Handle setting the page state for the next question
    const handleSetNextPageState = useCallback(
        (nextQuestionId: string | null | undefined, callback?: () => void) => {
            const latestQuestionList = Array.isArray(
                currentQuestionList.current,
            )
                ? currentQuestionList.current
                : questionList;
            const thisQuestion = latestQuestionList.find(
                (q) => q.questionId === nextQuestionId,
            );
            if (thisQuestion) {
                // Get next question data
                const {
                    questionType,
                    parentQuestion,
                    userAnswers,
                    prefillAnswers,
                    answerList,
                } = thisQuestion;
                // Default state
                let isLongContent = false;
                let isSmallContent = false;
                let continueVisible = true;
                // Determine long/small content state by screen & question type
                if (isMobile) {
                    // If follow up question, set long content
                    if (
                        questionType === QUIZ_QUESTION_TYPES.freeText &&
                        !!parentQuestion &&
                        questionList
                            .map((q) => q.questionId)
                            .includes(parentQuestion)
                    ) {
                        isLongContent = true;
                    }
                } else {
                    isLongContent =
                        QUIZ_LONG_CONTENT_QUESTION_TYPES.includes(questionType);
                    if (
                        questionType === QUIZ_QUESTION_TYPES.singleChoice &&
                        answerList.length <= QUIZ_SELECT_SMALL_ANSWER_LIMIT
                    ) {
                        isSmallContent = true;
                    }
                }
                // Determine continue visibility
                if (QUIZ_HIDE_CONTINUE_QUESTION_TYPES.includes(questionType)) {
                    continueVisible =
                        userAnswers.length > 0 || prefillAnswers.length > 0;
                }
                // Set next question state
                setIsLongContent(isLongContent);
                setIsSmallContent(isSmallContent);
                // Reset state
                setContinueText(null);
                setContinueVisible(continueVisible);
                setContinueDisabled(true);
                setIsOverflowing(false);
                setPageBottomInView(false);
                setPageRendered(false);
                // Clear prefill data if reached summary
                if (questionType === QUIZ_QUESTION_TYPES.prefilled) {
                    setCurrentPrefillParentId(null);
                    setCurrentPrefillSectionId(null);
                    setQueueUnansweredOnly(false);
                    setQueueQuestionList(null);
                }
                // Execute callback if exists
                if (typeof callback === 'function') callback();
            }
        },
        [isMobile, questionList],
    );

    // Question info
    // The current question id
    const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(
        null,
    );
    useEffect(() => {
        // If question id not set
        if (!currentQuestionId) {
            // Default to first question id
            let newCurrentQuestionId: string | null =
                questionList.length > 0
                    ? questionList[0]?.questionId
                        ? questionList[0].questionId
                        : null
                    : null;
            // Default to quiz current question id
            if (quizCurrentQuestionId) {
                newCurrentQuestionId = quizCurrentQuestionId;
            }
            // Check if quiz contains a prefill summary
            if (Object.keys(prefillById).length > 0) {
                const currentQuestionIndex = questionList.findIndex(
                    (q) => q.questionId === quizCurrentQuestionId,
                );
                Object.keys(prefillById)
                    .reverse()
                    .forEach((prefillQuestionId) => {
                        const prefillQuestionIndex = questionList.findIndex(
                            (q) => q.questionId === prefillQuestionId,
                        );
                        // Check if quiz current question is after prefill summary
                        if (currentQuestionIndex > prefillQuestionIndex) {
                            const { questionList: prefillQuestionList } =
                                prefillById[prefillQuestionId];
                            // Check if any prefill questions are not submitted, if so send them back to summary
                            if (
                                prefillQuestionList.some(
                                    (q) => q.userAnswers.length === 0,
                                )
                            ) {
                                newCurrentQuestionId = prefillQuestionId
                                    ? prefillQuestionId
                                    : null;
                            }
                        }
                    });
            }
            // Set page state
            handleSetNextPageState(newCurrentQuestionId);
            // Set current question
            setCurrentQuestionId(newCurrentQuestionId);
        }
    }, [
        currentQuestionId,
        quizCurrentQuestionId,
        questionList,
        prefillById,
        handleSetNextPageState,
    ]);

    // The current question index within the question list
    const currentQuestionIndex = questionList.findIndex(
        (q) => q.questionId === currentQuestionId,
    );
    const lastCompletedQuestionIndex = questionList.reduce(
        (maxIndex, question, i) =>
            question.userAnswers.length > 0 ? i : maxIndex,
        0,
    );

    const currentQuestionList = useRef<QuestionList | null>(null);
    useEffect(() => {
        currentQuestionList.current = questionList;
    }, [questionList]);

    // Prefill Ids
    const [currentPrefillParentId, setCurrentPrefillParentId] = useState<
        string | null
    >(null);
    const [currentPrefillSectionId, setCurrentPrefillSectionId] = useState<
        number | string | null
    >(null);
    // Prefill dictionary by parent question id
    const currentPrefillById = useRef<PrefillById>();
    useEffect(() => {
        currentPrefillById.current = prefillById;
    }, [prefillById]);

    // Question Queue - priority questions that need to be answered first
    // Allows questions to be queued for completion regardless of quiz progression
    const [queueUnansweredOnly, setQueueUnansweredOnly] =
        useState<boolean>(false);
    const [queueQuestionList, setQueueQuestionList] = useState<string[] | null>(
        null,
    );

    // Generates a queue of questions for a prefill section
    const generateQueueQuestionList = useCallback(
        (
            questionId: string | null,
            sectionId: string | number | null,
            unansweredOnly: boolean,
        ) => {
            // The list of questions in the queue
            const filterUnansweredQuestions = (question: QuestionRecord) => {
                // Check if both userAnswers and prefillAnswers are empty
                const hasNoAnswers =
                    question.prefillAnswers.length === 0 &&
                    question.userAnswers.length === 0;
                const childQuestions = questionList.filter(
                    (q) => q.parentQuestion === question.questionId,
                );
                // Check if the question has a child question with both userAnswers and prefillAnswers empty
                const hasEmptyChild = childQuestions?.some(
                    ({ prefillAnswers, userAnswers }) => {
                        return (
                            prefillAnswers.length === 0 &&
                            userAnswers.length === 0
                        );
                    },
                );

                // Return true if either of the conditions is met
                return hasNoAnswers || hasEmptyChild;
            };

            // Check latest prefill data
            const latestPrefillById = currentPrefillById.current;
            if (questionId && sectionId && latestPrefillById?.[questionId]) {
                const { questionList } =
                    latestPrefillById[questionId].sectionById[sectionId] || {};
                // If any unanswered, do them first
                if (
                    unansweredOnly ||
                    questionList.some(
                        ({ prefillAnswers, userAnswers }) =>
                            prefillAnswers.length === 0 &&
                            userAnswers.length === 0,
                    )
                ) {
                    const filteredQuestionList = questionList.filter(
                        filterUnansweredQuestions,
                    );

                    return filteredQuestionList.map((q) => q.questionId);
                } else {
                    return questionList.map((q) => q.questionId);
                }
            } else {
                return [];
            }
        },
        [questionList],
    );
    // Updates queue question list to allow for dynamically added/removed questions
    const getUpdatedQueueQuestionList = useCallback(
        (queueUnansweredOnly: boolean) => {
            if (
                Array.isArray(queueQuestionList) &&
                currentQuestionList.current
            ) {
                let newQueueQuestionList = queueQuestionList;
                // Remove any records removed by comparing to main question list
                const latestQuestionList = currentQuestionList.current.map(
                    (q) => q.questionId,
                );
                newQueueQuestionList = newQueueQuestionList.filter((q) =>
                    latestQuestionList.includes(q),
                );
                // check if any records added by regenerating queue
                const tempQueueQuestions = generateQueueQuestionList(
                    currentPrefillParentId,
                    currentPrefillSectionId,
                    queueUnansweredOnly,
                );
                const missingQueueQuestions = tempQueueQuestions.filter(
                    (q) => !newQueueQuestionList.includes(q),
                );
                newQueueQuestionList = newQueueQuestionList.concat(
                    missingQueueQuestions,
                );
                // Sort modified queue to maintain original order
                newQueueQuestionList = newQueueQuestionList.sort((a, b) => {
                    const indexA = latestQuestionList.indexOf(a);
                    const indexB = latestQuestionList.indexOf(b);
                    if (indexA === -1 && indexB === -1) {
                        return 0;
                    } else if (indexA === -1) {
                        return 1;
                    } else if (indexB === -1) {
                        return -1;
                    }

                    return indexA - indexB;
                });

                return newQueueQuestionList;
            }

            return null;
        },
        [
            currentPrefillParentId,
            currentPrefillSectionId,
            queueQuestionList,
            generateQueueQuestionList,
        ],
    );
    // Whether or not currently within a queue
    const inQueue = useMemo(
        () => Array.isArray(queueQuestionList),
        [queueQuestionList],
    );
    // The current index within the queue
    const queueQuestionListIndex = useMemo(
        () =>
            !!currentQuestionId &&
            Array.isArray(queueQuestionList) &&
            queueQuestionList.includes(currentQuestionId)
                ? queueQuestionList.indexOf(currentQuestionId)
                : -1,
        [queueQuestionList, currentQuestionId],
    );

    // Content state
    const [pageTransitioning, setPageTransitioning] = useState(false);
    const [isLongContent, setIsLongContent] = useState(false);
    const [isSmallContent, setIsSmallContent] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [pageBottomInView, setPageBottomInView] = useState(false);
    const [pageRendered, setPageRendered] = useState(false);

    // Footer state
    const [backDisabled, setBackDisabled] = useState(true);
    const [backVisible, setBackVisible] = useState(false);
    const [forwardDisabled, setForwardDisabled] = useState(true);
    const [forwardVisible, setForwardVisible] = useState(false);
    const [continueText, setContinueText] = useState<string | null>(null);
    const [continueDisabled, setContinueDisabled] = useState(true);
    const [continueVisible, setContinueVisible] = useState(true);
    const [acceptVisible, setAcceptVisible] = useState(false);

    // This is used if the user is not in a queue and needs to progress to the current due question
    const handleNavigateToQuestion = useCallback(
        (questionId?: string | null) => {
            const toQuestionId = questionId
                ? questionId
                : quizCurrentQuestionId
                  ? quizCurrentQuestionId
                  : null;
            if (toQuestionId) {
                setPageTransitioning(true);
                setTimeout(() => {
                    // Set next page state
                    setLoading(false);
                    handleSetNextPageState(toQuestionId, () => {
                        setCurrentQuestionId(toQuestionId);
                        setTimeout(() => {
                            setPageTransitioning(false);
                        }, 200);
                    });
                }, 200);
            }
        },
        [quizCurrentQuestionId, handleSetNextPageState],
    );

    // Navigate to the next question
    const handleNavigateNextQuestion = useCallback(() => {
        // Use updated question list if available
        const latestQuestionList = Array.isArray(currentQuestionList.current)
            ? currentQuestionList.current
            : questionList;
        const currentQuestionIndex = latestQuestionList.findIndex(
            (q) => q.questionId === currentQuestionId,
        );
        let navigationPossible = false;
        let toQuestionId: string | undefined | null = null;
        let nextQuestionIndex: number | undefined = undefined;

        // Check if in queue or list
        if (
            inQueue &&
            currentPrefillParentId !== null &&
            currentPrefillSectionId !== null &&
            currentQuestionId !== null
        ) {
            // The queue must be updated here, to allow for dynamic questions to be added/removed
            const updatedQueueQuestionList =
                getUpdatedQueueQuestionList(queueUnansweredOnly);
            setQueueQuestionList(updatedQueueQuestionList);
            const queueQuestionListIndex =
                updatedQueueQuestionList !== null
                    ? updatedQueueQuestionList.indexOf(currentQuestionId)
                    : null;
            // Redirect user to next queue question if exists
            if (
                Array.isArray(updatedQueueQuestionList) &&
                queueQuestionListIndex !== null &&
                queueQuestionListIndex < updatedQueueQuestionList.length - 1
            ) {
                nextQuestionIndex = queueQuestionListIndex + 1;
                const nextQuestionId =
                    updatedQueueQuestionList[queueQuestionListIndex + 1];
                navigationPossible = true;
                toQuestionId = nextQuestionId;
            } else {
                // Default redirect user to prefill parent
                navigationPossible = true;
                toQuestionId = currentPrefillParentId;
            }
        } else {
            // Iterate through next questions until valid match found
            let indexOffset = 1;
            while (
                !navigationPossible &&
                currentQuestionIndex + indexOffset < latestQuestionList.length
            ) {
                const thisQuestion =
                    latestQuestionList[currentQuestionIndex + indexOffset];
                if (thisQuestion) {
                    const {
                        questionId: nextQuestionId,
                        prefillNeeded,
                        questionType,
                        parentQuestion,
                    } = thisQuestion;
                    // For number rating questions
                    if (questionType === QUIZ_QUESTION_TYPES.numberRating) {
                        // Skip over children
                        if (!parentQuestion) {
                            navigationPossible = true;
                            toQuestionId = nextQuestionId;
                            nextQuestionIndex =
                                currentQuestionIndex + indexOffset;
                        }
                    } // skip over password question if already answered
                    else if (
                        questionType === QUIZ_QUESTION_TYPES.password ||
                        questionType === QUIZ_QUESTION_TYPES.leadForm ||
                        questionType === QUIZ_QUESTION_TYPES.leadFormSSO ||
                        questionType === QUIZ_QUESTION_TYPES.passwordField
                    ) {
                        if (
                            !submittedQuestionIds.includes(nextQuestionId) &&
                            !prefillQuestionIds.includes(nextQuestionId)
                        ) {
                            navigationPossible = true;
                            toQuestionId = nextQuestionId;
                            nextQuestionIndex =
                                currentQuestionIndex + indexOffset;
                        }
                    } else {
                        // TODO, do we want this, or do we want to show user already answered prefill questions?
                        // This will skip over already answered prefill needed questions to prevent the user having to see them again
                        const isPrefill =
                            prefillQuestionIds.includes(nextQuestionId);
                        if (!(prefillNeeded && isPrefill)) {
                            navigationPossible = true;
                            toQuestionId = nextQuestionId;
                            nextQuestionIndex =
                                currentQuestionIndex + indexOffset;
                        }
                    }
                }
                indexOffset++;
            }
        }

        // Navigate if possible
        if (navigationPossible) {
            const newUrl = `${quizBaseUrl}&question=${nextQuestionIndex}`;
            window.history.pushState(null, '', newUrl);
            handleNavigateToQuestion(toQuestionId);
        } else if (quizCurrentQuestionId) {
            const newUrl = `${quizBaseUrl}&question=${nextQuestionIndex}`;
            window.history.pushState(null, '', newUrl);
            handleNavigateToQuestion(quizCurrentQuestionId);
        }
    }, [
        quizBaseUrl,
        submittedQuestionIds,
        currentQuestionId,
        quizCurrentQuestionId,
        questionList,
        currentPrefillParentId,
        currentPrefillSectionId,
        prefillQuestionIds,
        inQueue,
        queueUnansweredOnly,
        handleNavigateToQuestion,
        getUpdatedQueueQuestionList,
    ]);

    // Navigate to the previous question
    const handleNavigatePrevQuestion = useCallback(() => {
        // if quiz is on the final question
        const latestQuestionList = Array.isArray(currentQuestionList.current)
            ? currentQuestionList.current
            : questionList;
        const currentQuestionIndex = latestQuestionList.findIndex(
            (q) => q.questionId === currentQuestionId,
        );
        let navigationPossible = false;
        let toQuestionId: string | null | undefined = null;
        let previousQuestionIndex: number | undefined = undefined;

        // Check if in queue or list
        if (Array.isArray(queueQuestionList) && inQueue) {
            // Find previous queue question
            if (queueQuestionListIndex > 0) {
                navigationPossible = true;
                const prevQuestionId =
                    queueQuestionList[queueQuestionListIndex - 1];
                toQuestionId = prevQuestionId;
                previousQuestionIndex = queueQuestionListIndex - 1;
            } else if (currentPrefillParentId) {
                navigationPossible = true;
                toQuestionId = currentPrefillParentId;
            }
        } else {
            // Iterate through previous questions until valid match found
            let indexOffset = 1;
            while (
                !navigationPossible &&
                currentQuestionIndex - indexOffset > -1
            ) {
                const thisQuestion =
                    latestQuestionList[currentQuestionIndex - indexOffset];
                if (thisQuestion) {
                    const {
                        questionId: prevQuestionId,
                        questionType,
                        parentQuestion,
                        prefillNeeded,
                    } = thisQuestion;
                    // Skip loading screens and password questions
                    if (
                        questionType === QUIZ_QUESTION_TYPES.loading ||
                        questionType === QUIZ_QUESTION_TYPES.password ||
                        questionType === QUIZ_QUESTION_TYPES.leadForm ||
                        questionType === QUIZ_QUESTION_TYPES.leadFormSSO ||
                        questionType === QUIZ_QUESTION_TYPES.passwordField
                    ) {
                        // For number rating questions
                    } else if (
                        questionType === QUIZ_QUESTION_TYPES.numberRating
                    ) {
                        // Navigate to parent question
                        if (parentQuestion) {
                            navigationPossible = true;
                            toQuestionId = parentQuestion;
                            previousQuestionIndex =
                                currentQuestionIndex - indexOffset;
                        }
                    } else {
                        navigationPossible = true;
                        const isPrefill =
                            prefillQuestionIds.includes(prevQuestionId);
                        // Skip prefill questions - send user to prefill parent
                        if (prefillNeeded && isPrefill) {
                            if (parentQuestion) {
                                toQuestionId = parentQuestion;
                            }
                        } else {
                            toQuestionId = prevQuestionId;
                        }
                        previousQuestionIndex =
                            currentQuestionIndex - indexOffset;
                    }
                }
                indexOffset++;
            }
        }

        // Navigate if possible
        if (navigationPossible) {
            const newUrl = `${quizBaseUrl}&question=${previousQuestionIndex}`;
            window.history.pushState(null, '', newUrl);
            handleNavigateToQuestion(toQuestionId);
        }
    }, [
        quizBaseUrl,
        currentPrefillParentId,
        prefillQuestionIds,
        currentQuestionId,
        questionList,
        queueQuestionList,
        queueQuestionListIndex,
        inQueue,
        handleNavigateToQuestion,
    ]);

    useEffect(() => {
        // handle browser back button
        const handlePopState = () => {
            handleNavigatePrevQuestion();
        };

        const onPopState = () => handlePopState();
        window.addEventListener('popstate', onPopState);

        return () => {
            window.removeEventListener('popstate', onPopState);
        };
    }, [
        handleNavigatePrevQuestion,
        quizFlow,
        quizType,
        currentQuestionList,
        currentQuestionId,
    ]);

    // Handle setting the queue of questions to take priority over the question list
    const handleSetQueueQuestions = useCallback(
        (
            questionId: string,
            sectionId: number | string,
            unansweredOnly: boolean,
        ) => {
            // generate the queue
            const newQueueQuestionList = generateQueueQuestionList(
                questionId,
                sectionId,
                unansweredOnly,
            );
            setQueueQuestionList(newQueueQuestionList);
            if (newQueueQuestionList[0]) {
                const toQuestionId = newQueueQuestionList[0];
                setCurrentPrefillParentId(questionId);
                setCurrentPrefillSectionId(sectionId);
                setQueueUnansweredOnly(unansweredOnly);
                handleNavigateToQuestion(toQuestionId);
            }
        },
        [generateQueueQuestionList, handleNavigateToQuestion],
    );

    // Handle submit
    const handleSubmitAnswer = useCallback(
        (
            answer: SubmitAnswerParams,
            callback?: (response: QuizRecord) => void,
            options?: { signal: AbortSignal },
        ) => {
            setLoading(true);
            submitAnswer({
                ...answer,
                callback: (response: QuizRecord) => {
                    // Redirect user if requested by API
                    // Used for redirecting once the quiz is completed
                    if (response?.redirectUrl) {
                        if (!response?.gateType) {
                            trackCompletedQuiz(
                                Vertical[quizType.toUpperCase()],
                                {
                                    quiz_token: quizToken,
                                    quiz_name: `${quizName} - ${quizVersion}`,
                                    label: 'Quiz Completed',
                                },
                            );
                        } else {
                            trackGateTriggered(
                                Vertical[quizType.toUpperCase()],
                                {
                                    quiz_token: quizToken,
                                    quiz_name: `${quizName} - ${quizVersion}`,
                                    label: 'Gate Triggered',
                                    gate_type: response.gateType,
                                    gate_source: 'Quiz',
                                },
                            );
                        }
                        window.location.href = response.redirectUrl;
                    }
                    // Execute custom callback if one exists
                    if (typeof callback === 'function') callback(response);
                    // Generate question list from response
                    const newQuestionList = generateQuestionList(response);
                    // Generate prefill data
                    const { prefillById: newPrefillById } =
                        generatePrefillData(newQuestionList);
                    // Set ref so that the updated question list can be accessed immediately before rerender
                    currentQuestionList.current = newQuestionList;
                    currentPrefillById.current = newPrefillById;
                    // Skip these question types as nav is handled within the field itself
                    if (
                        QUIZ_SKIP_NAVIGATION_QUESTION_TYPES.includes(
                            questionById[answer.questionId].questionType,
                        )
                    ) {
                        setLoading(false);
                    } else {
                        handleNavigateNextQuestion();
                    }
                },
                options,
            });
        },
        [
            quizToken,
            submitAnswer,
            quizType,
            quizName,
            quizVersion,
            generatePrefillData,
            generateQuestionList,
            handleNavigateNextQuestion,
            questionById,
        ],
    );

    // Create context to pass data, handlers and state to questions
    const quizContext: QuizContextType = useMemo(() => {
        const currentQuestion = currentQuestionId
            ? questionById[currentQuestionId]
            : null;
        const completedQuestionList = questionList.slice(
            0,
            lastCompletedQuestionIndex + 1,
        );

        return {
            quizToken,
            quizFlow,
            quizType,
            quizName,
            quizVersion,
            iterationOfProgressBar,
            currentQuestionId,
            currentQuestion,
            currentQuestionIndex,
            lastCompletedQuestionIndex,
            submittedQuestionIds,
            questionById,
            questionList,
            completedQuestionList,
            prefillById,
            prefillQuestionIds,
            sectionById,
            sectionList,
            medications,
            plans,
            inQueue,
            handleSubmitAnswer,
            handleNavigateToQuestion,
            handleNavigatePrevQuestion,
            handleNavigateNextQuestion,
            handleSetQueueQuestions,
            state: {
                // Use state in component like this
                // const [forwardVisible, setForwardVisible] = state.forwardVisible;
                pageTransitioning: [pageTransitioning, setPageTransitioning],
                pageBottomInView: [pageBottomInView, setPageBottomInView],
                pageRendered: [pageRendered, setPageRendered],
                isLongContent: [isLongContent, setIsLongContent],
                isSmallContent: [isSmallContent, setIsSmallContent],
                isOverflowing: [isOverflowing, setIsOverflowing],
                backDisabled: [backDisabled, setBackDisabled],
                backVisible: [backVisible, setBackVisible],
                forwardDisabled: [forwardDisabled, setForwardDisabled],
                forwardVisible: [forwardVisible, setForwardVisible],
                continueText: [continueText, setContinueText],
                continueDisabled: [continueDisabled, setContinueDisabled],
                continueVisible: [continueVisible, setContinueVisible],
                acceptVisible: [acceptVisible, setAcceptVisible],
                loading: [loading, setLoading],
            },
        };
    }, [
        quizToken,
        quizFlow,
        quizType,
        quizName,
        quizVersion,
        iterationOfProgressBar,
        currentQuestionId,
        currentQuestionIndex,
        lastCompletedQuestionIndex,
        submittedQuestionIds,
        questionById,
        questionList,
        prefillById,
        prefillQuestionIds,
        sectionById,
        sectionList,
        medications,
        plans,
        inQueue,
        handleSubmitAnswer,
        handleNavigateToQuestion,
        handleNavigatePrevQuestion,
        handleNavigateNextQuestion,
        handleSetQueueQuestions,
        pageTransitioning,
        pageBottomInView,
        setPageBottomInView,
        pageRendered,
        setPageRendered,
        isLongContent,
        setIsLongContent,
        isSmallContent,
        setIsSmallContent,
        isOverflowing,
        setIsOverflowing,
        backDisabled,
        setBackDisabled,
        backVisible,
        setBackVisible,
        forwardDisabled,
        setForwardDisabled,
        forwardVisible,
        setForwardVisible,
        continueText,
        setContinueText,
        continueDisabled,
        setContinueDisabled,
        continueVisible,
        setContinueVisible,
        acceptVisible,
        setAcceptVisible,
        loading,
        setLoading,
    ]);

    return (
        <QueryClientProvider client={queryClient}>
            <QuizContext.Provider value={quizContext}>
                {startQuizLoading ? (
                    <div
                        className="container flex items-center justify-center"
                        style={{ height: '100vh' }}
                    >
                        <Loading />
                    </div>
                ) : (
                    <QuizQuestion
                        auspostPostcodeSearchEnabled={
                            auspostPostcodeSearchEnabled
                        }
                    />
                )}
            </QuizContext.Provider>
        </QueryClientProvider>
    );
};

export default Quiz;
